import React from "react";
import { ArrowRight, Mail, ShieldCheck, UserCheck, Clock } from "lucide-react";
import SEO from "../components/SEO";
import SectionReveal from "../components/SectionReveal";
import QuoteForm from "../components/forms/QuoteForm";
import { SEO as META, SITE } from "../content";

const TRUST_BULLETS = [
  {
    icon: UserCheck,
    title: "Founder-led service",
    body: "Talk to the operator, not a call centre.",
  },
  {
    icon: ShieldCheck,
    title: "WHMIS · TDG · BBP certified",
    body: "Certified handling for medical, regulated and dangerous goods.",
  },
  {
    icon: Clock,
    title: "Same-day capable",
    body: "Local pickup and delivery across Milton, Halton & GTA.",
  },
];

export default function Quote() {
  return (
    <>
      <SEO
        title="Get a Quote — Ridgehawk Logistics"
        description="Request a custom quote for same-day courier, medical transport, last-mile delivery, B2B freight or dedicated routes in Milton & the GTA."
        path="/quote"
      />

      {/* HERO */}
      <section
        className="relative bg-ridge-charcoal text-white"
        data-testid="quote-hero"
      >
        <div className="container-pad py-20 md:py-24 lg:py-28">
          <SectionReveal>
            <div className="max-w-3xl">
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-ridge-amber">
                Get a Quote
              </span>
              <h1 className="font-display mt-4 text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Get a Detailed Quote
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
                Tell us where it's going, what you're moving and when. We respond
                within one business hour during operating hours.
              </p>
            </div>
          </SectionReveal>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1 bg-ridge-amber" />
      </section>

      {/* FORM + INFO */}
      <section className="section-pad bg-white" data-testid="quote-form-section">
        <div className="container-pad">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <SectionReveal className="lg:col-span-8">
              <QuoteForm />
            </SectionReveal>

            <SectionReveal className="lg:col-span-4" delay={0.08}>
              <aside
                className="sticky top-28 space-y-6 rounded-md border border-ridge-border bg-ridge-stone p-6 md:p-8"
                data-testid="quote-info-aside"
              >
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-ridge-amber">
                    Why Ridgehawk
                  </span>
                  <h2 className="font-display mt-3 text-2xl font-bold leading-tight tracking-tight text-ridge-charcoal md:text-3xl">
                    Built for businesses that can't afford a bad handoff.
                  </h2>
                </div>

                <ul className="space-y-5">
                  {TRUST_BULLETS.map(({ icon: Icon, title, body }) => (
                    <li key={title} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-ridge-charcoal text-white">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span>
                        <span className="block font-display text-sm font-semibold text-ridge-charcoal">
                          {title}
                        </span>
                        <span className="mt-1 block text-sm leading-relaxed text-ridge-text">
                          {body}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="border-t border-ridge-border pt-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ridge-charcoal">
                    Business hours
                  </p>
                  <p className="mt-2 text-sm text-ridge-text">{SITE.hours}</p>
                  <p className="text-xs text-ridge-muted">
                    Saturday by arrangement.
                  </p>
                </div>

                <div className="border-t border-ridge-border pt-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ridge-charcoal">
                    Need help?
                  </p>
                  <a
                    href={`mailto:${SITE.email}`}
                    className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-ridge-charcoal transition-colors hover:text-ridge-amber"
                    data-testid="quote-info-email"
                  >
                    <Mail className="h-4 w-4 text-ridge-amber" aria-hidden="true" />
                    {SITE.email}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </a>
                </div>
              </aside>
            </SectionReveal>
          </div>
        </div>
      </section>
    </>
  );
}
